import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { ScrollArea } from '@/components/ui/ScrollArea';
import {
  electronApis,
  type AppWithExclusionState,
} from '@/features/electron-api';
import { SettingsSection } from '@/features/settings/settings-section';
import { logError } from '@/lib/errorLogging';
import { inElectron, isWindows } from '@/utils';

import { teamApis } from '../team.query';
import { teamStore } from '../team.store';
import { type EnforcedExcludedApp, type TeamAppList } from '../team.types';

import {
  platformGap,
  PLATFORM_LABELS,
  singlePlatformWarning,
  TeamPlatformWarning,
} from './TeamPlatformWarning';

interface TeamEnforcedExclusionsCardProps {
  teamId: string;
  apps: EnforcedExcludedApp[];
  domains: string[];
}

export const TeamEnforcedExclusionsCard = observer(
  function TeamEnforcedExclusionsCard({
    teamId,
    apps,
    domains,
  }: TeamEnforcedExclusionsCardProps) {
    const gap = platformGap(apps);

    return (
      <YStack className="w-full gap-3">
        <SettingsSection.GroupHeader>
          Team Exclusions
        </SettingsSection.GroupHeader>

        <SettingsSection.Group>
          <SettingsSection.Item direction="vertical">
            <SettingsSection.ItemHeader>
              <SettingsSection.ItemDescription>
                These apps and sites are excluded from capture for every team
                member and cannot be overridden.
              </SettingsSection.ItemDescription>
            </SettingsSection.ItemHeader>

            {gap && (
              <YStack className="w-full pt-4">
                <TeamPlatformWarning message={singlePlatformWarning(gap)} />
              </YStack>
            )}
            <TeamAppsGroup
              teamId={teamId}
              list="exclusions"
              apps={apps}
              emptyLabel="No apps excluded for the team"
            />
            <ExcludedSitesGroup teamId={teamId} domains={domains} />
          </SettingsSection.Item>
        </SettingsSection.Group>
      </YStack>
    );
  }
);

const ExcludedSitesGroup = observer(function ExcludedSitesGroup({
  teamId,
  domains,
}: {
  teamId: string;
  domains: string[];
}) {
  const [inputValue, setInputValue] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleAdd = async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    try {
      await teamApis.addEnforcedExcludedDomain(teamId, trimmed);
      setInputValue('');
    } catch {
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <YStack className="w-full gap-2 pt-4">
      <span className="text-sm font-medium">Sites</span>

      <XStack className="gap-2 items-center w-full">
        <Input
          type="text"
          placeholder="Add site (e.g., payroll.acme.com)"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          className="flex-1"
          disabled={submitting}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleAdd();
          }}
        />
        <Button
          variant="outline"
          className="text-sm whitespace-nowrap"
          onClick={handleAdd}
          isLoading={submitting}
          disabled={submitting || !inputValue.trim()}
        >
          Add
        </Button>
      </XStack>

      <YStack className="w-full gap-0.5">
        {domains.length === 0 && (
          <p className="text-xs text-foreground-muted py-1">
            No sites excluded for the team
          </p>
        )}
        {domains.map((domain) => (
          <ListEntry
            key={domain}
            label={domain}
            isRemoving={teamStore.mutatingDomain === domain}
            onRemove={() =>
              teamApis.removeEnforcedExcludedDomain(teamId, domain)
            }
          />
        ))}
      </YStack>
    </YStack>
  );
});

export const TeamAppsGroup = observer(function TeamAppsGroup({
  teamId,
  list,
  apps,
  emptyLabel,
  disabled = false,
}: {
  teamId: string;
  list: TeamAppList;
  apps: EnforcedExcludedApp[];
  emptyLabel: string;
  disabled?: boolean;
}) {
  const [installedApps, setInstalledApps] = useState<AppWithExclusionState[]>(
    []
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  useEffect(() => {
    if (!inElectron()) return;
    electronApis.appExclusion
      .getAppExclusions()
      .then(setInstalledApps)
      .catch((err) => logError(err, 'Failed to load installed apps'));
  }, []);

  const addedBundleIds = useMemo(
    () => new Set(apps.map((app) => app.bundle_id)),
    [apps]
  );

  const matchingApps = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return installedApps.filter(
      (app) =>
        !addedBundleIds.has(app.identifier) &&
        (!query || app.displayName.toLowerCase().includes(query))
    );
  }, [installedApps, searchQuery, addedBundleIds]);

  const handleAdd = async (app: AppWithExclusionState) => {
    setSearchQuery('');
    try {
      await teamApis.addTeamApp(teamId, list, {
        bundle_id: app.identifier,
        name: app.displayName,
        platform: isWindows() ? 'windows' : 'mac',
      });
    } catch {}
  };

  return (
    <YStack className="w-full gap-2 pt-4">
      <span className="text-sm font-medium">Apps</span>

      <Input
        type="text"
        placeholder="Select or search an app..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        onFocus={() => setIsPickerOpen(true)}
        onBlur={() => setIsPickerOpen(false)}
        disabled={disabled}
        className="w-full"
      />

      {isPickerOpen && (
        <ScrollArea className="max-h-48 bg-surface border border-border rounded-lg p-1">
          {matchingApps.length === 0 ? (
            <p className="text-xs text-foreground-muted p-1">
              No matching apps
            </p>
          ) : (
            <YStack className="gap-0.5">
              {matchingApps.map((app) => (
                <XStack
                  key={app.identifier}
                  className="items-center gap-2 rounded px-2 py-1 hover:bg-surface-hover cursor-pointer"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleAdd(app)}
                >
                  <img src={app.icon} alt="" className="size-5 rounded" />
                  <span className="text-sm truncate">{app.displayName}</span>
                </XStack>
              ))}
            </YStack>
          )}
        </ScrollArea>
      )}

      <YStack className="w-full gap-0.5">
        {apps.length === 0 && (
          <p className="text-xs text-foreground-muted py-1">{emptyLabel}</p>
        )}
        {apps.map((app) => (
          <ListEntry
            key={app.bundle_id}
            label={app.name}
            detail={PLATFORM_LABELS[app.platform]}
            isRemoving={teamStore.mutatingDomain === app.bundle_id}
            onRemove={() => teamApis.removeTeamApp(teamId, list, app.bundle_id)}
          />
        ))}
      </YStack>
    </YStack>
  );
});

type ListEntryProps = {
  label: string;
  detail?: string;
  isRemoving: boolean;
  onRemove: () => void;
};

export const ListEntry = ({
  label,
  detail,
  isRemoving,
  onRemove,
}: ListEntryProps) => {
  return (
    <XStack className="w-full items-center justify-between gap-2 py-1">
      <XStack className="min-w-0 items-baseline gap-2">
        <span className="text-sm truncate">{label}</span>
        {detail && (
          <span className="text-xs text-foreground-muted truncate">
            {detail}
          </span>
        )}
      </XStack>
      <button
        onClick={onRemove}
        disabled={isRemoving}
        className="shrink-0 p-0.5 hover:bg-surface-hover rounded disabled:opacity-50 disabled:cursor-not-allowed"
        aria-label={`Remove ${label}`}
      >
        <Icons.cancel size={12} className="text-foreground-muted" />
      </button>
    </XStack>
  );
};
